<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import RichTextContent from '@/components/editors/RichTextContent.vue';
import FileAttachmentList from '@/components/files/FileAttachmentList.vue';
import { formatDate } from '~/lib/dateFormat';
import type { Approval } from '~/types/models';

interface Props {
    approval: Approval;
    canRespond?: boolean;
    canRemind?: boolean;
    variant?: 'card' | 'row';
}

const props = withDefaults(defineProps<Props>(), {
    canRespond: false,
    canRemind: false,
    variant: 'card',
});

const emit = defineEmits<{
    (e: 'respond', approval: Approval): void;
    (e: 'remind', approval: Approval): void;
}>();

const statusVariant = computed(() => {
    if (props.approval.status === 'approved') return 'default';
    if (props.approval.status === 'rejected') return 'destructive';
    return 'secondary';
});
</script>

<template>
    <div :class="variant === 'row' ? 'space-y-2 py-4' : 'space-y-2 rounded-lg border p-3'">
        <div class="flex items-center justify-between gap-2">
            <p class="text-sm font-medium">{{ approval.stageName }}</p>
            <Badge :variant="statusVariant">{{ approval.status }}</Badge>
        </div>

        <RichTextContent v-if="approval.message" :content="approval.message" />
        <FileAttachmentList :attachments="approval.attachments" />

        <p class="text-xs text-muted-foreground">
            Requested by {{ approval.requestedByName || 'Unknown' }} on {{ formatDate(approval.createdAt, 'datetime') }}
        </p>

        <p v-if="approval.respondedAt" class="text-xs text-muted-foreground">
            {{ approval.status === 'approved' ? 'Approved' : 'Rejected' }} by {{ approval.reviewedByName || 'Unknown' }} on
            {{ formatDate(approval.respondedAt, 'datetime') }}
        </p>

        <p v-if="approval.reviewNote" class="text-xs text-muted-foreground">
            Note: <span class="text-foreground">{{ approval.reviewNote }}</span>
        </p>

        <div v-if="canRespond || (canRemind && approval.status === 'pending')" class="flex flex-wrap gap-2 pt-1">
            <Button v-if="canRespond" size="sm" variant="outline" @click="emit('respond', approval)">
                {{ approval.status === 'pending' ? 'Review' : 'Edit response' }}
            </Button>
            <Button v-if="canRemind && approval.status === 'pending'" size="sm" variant="outline" @click="emit('remind', approval)">Send reminder</Button>
        </div>
    </div>
</template>
